/**
 * Enter Tasks — UI-only store (state only, no API calls). Task DATA comes from
 * React Query (`useTasksQuery`); this holds just the sidebar's view state:
 * the severity filter and the set of tasks with an in-flight close (optimistic).
 *
 * Convention (matches real-time-mode-store): atomic selectors only — never
 * destructure the store in a component.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EnterTask, TaskTypeId } from '@ctrl-enter/types';

export type SeverityFilter = 'all' | EnterTask['severity'];
/**
 * Which TYPE chip is selected — Halts / Denials / Rejections / …
 *
 * Platform's redesign organises the worklist by task TYPE rather than by
 * priority, because type is what determines the work: a halt is fixed in the
 * EMR, a denial is appealed, missing records are uploaded. Severity says how
 * loud a task is; type says what you do about it. Kept alongside the severity
 * filter rather than replacing it outright so the two can compose.
 */
export type TaskTypeFilter = TaskTypeId;
export type TaskScope = 'my' | 'all';

type EnterTasksStore = {
  filter: SeverityFilter;
  setFilter: (f: SeverityFilter) => void;
  typeFilter: TaskTypeFilter;
  setTypeFilter: (t: TaskTypeFilter) => void;
  /** 'my' (default) = the caller's tasks; 'all' = the whole team's. */
  scope: TaskScope;
  setScope: (s: TaskScope) => void;
  /** Task ids whose close request is in flight (row shows a spinner + is disabled). */
  closingIds: Set<string>;
  markClosing: (id: string) => void;
  clearClosing: (id: string) => void;
  reset: () => void;
};

export const useEnterTasksStore = create<EnterTasksStore>()(
  devtools(
    set => ({
      filter: 'all',
      typeFilter: 'all',
      setFilter: filter => set({ filter }),
      setTypeFilter: typeFilter => set({ typeFilter }),
      scope: 'my',
      setScope: scope => set({ scope }),
      closingIds: new Set<string>(),
      markClosing: id =>
        set(s => ({ closingIds: new Set(s.closingIds).add(id) })),
      clearClosing: id =>
        set(s => {
          const next = new Set(s.closingIds);
          next.delete(id);
          return { closingIds: next };
        }),
      reset: () =>
        set({ filter: 'all', typeFilter: 'all', scope: 'my', closingIds: new Set<string>() }),
    }),
    { name: 'enter-tasks-store' }
  )
);

// ─── Atomic selectors (never destructure the store) ─────────────────────────
export const useTasksFilter = () => useEnterTasksStore(s => s.filter);
export const useSetTasksFilter = () => useEnterTasksStore(s => s.setFilter);
export const useTasksTypeFilter = () => useEnterTasksStore(s => s.typeFilter);
export const useSetTasksTypeFilter = () => useEnterTasksStore(s => s.setTypeFilter);
export const useTasksScope = () => useEnterTasksStore(s => s.scope);
export const useSetTasksScope = () => useEnterTasksStore(s => s.setScope);
export const useTasksClosingIds = () => useEnterTasksStore(s => s.closingIds);
export const useMarkTaskClosing = () => useEnterTasksStore(s => s.markClosing);
export const useClearTaskClosing = () => useEnterTasksStore(s => s.clearClosing);
